'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import { useEffects } from '@/contexts/EffectsContext';
import { prefersReducedMotion, splitWords, splitChars } from '@/lib/effects';

gsap.registerPlugin(ScrollTrigger);

export default function EffectsInit() {
  const { isLoaded } = useEffects();
  const lenisRef = useRef<Lenis | null>(null);

  useEffect(() => {
    if (!isLoaded || typeof window === 'undefined') return;

    const reduced = prefersReducedMotion();
    const dur = reduced ? 0.01 : 0.8;
    const cleanups: (() => void)[] = [];

    /* ═══════ LENIS SMOOTH SCROLL ═══════ */
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothTouch: false,
    });
    lenisRef.current = lenis;
    lenis.on('scroll', ScrollTrigger.update);
    const lenisRaf = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(lenisRaf);
    gsap.ticker.lagSmoothing(0);

    /* ═══════ HERO REVEAL (homepage only) ═══════ */
    const heroWords = document.querySelectorAll<HTMLElement>('.hero-word-inner');
    if (heroWords.length && !reduced) {
      gsap.to(heroWords, { y: 0, duration: 0.75, stagger: 0.15, ease: 'power3.out', delay: 0.15 });
      gsap.to('.hero__subtitle', { opacity: 1, y: 0, duration: 0.6, delay: 0.65, ease: 'power2.out' });
      gsap.to('.hero__cta', { opacity: 1, y: 0, duration: 0.5, delay: 0.9, ease: 'power2.out' });
      gsap.to('.hero__scroll', { opacity: 1, duration: 0.5, delay: 1.3, ease: 'power2.out' });
    } else if (heroWords.length) {
      gsap.set(heroWords, { y: 0 });
      gsap.set('.hero__subtitle, .hero__cta, .hero__scroll', { opacity: 1, y: 0 });
    }

    /* ═══════ UNIVERSAL SCROLL REVEAL ═══════ */
    // [data-reveal] — single elements fade-up
    gsap.utils.toArray<HTMLElement>('[data-reveal]').forEach((el) => {
      gsap.fromTo(el,
        { y: reduced ? 0 : 50, opacity: 0 },
        {
          y: 0, opacity: 1, duration: dur, ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 90%', once: true },
        }
      );
    });

    // [data-reveal-group] — children stagger
    gsap.utils.toArray<HTMLElement>('[data-reveal-group]').forEach((group) => {
      const children = Array.from(group.children) as HTMLElement[];
      gsap.fromTo(children,
        { y: reduced ? 0 : 50, opacity: 0 },
        {
          y: 0, opacity: 1, duration: dur, stagger: reduced ? 0 : 0.12, ease: 'power3.out',
          scrollTrigger: { trigger: group, start: 'top 88%', once: true },
        }
      );
    });

    /* ═══════ TEXT ANIMATIONS ═══════ */
    // [data-split-chars] — character reveal
    gsap.utils.toArray<HTMLElement>('[data-split-chars]').forEach((el) => {
      const chars = splitChars(el);
      if (!chars.length) return;
      gsap.fromTo(chars,
        { y: reduced ? 0 : 25, opacity: 0 },
        {
          y: 0, opacity: 1, duration: reduced ? 0.01 : 0.45, stagger: reduced ? 0 : 0.025, ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 85%', once: true },
        }
      );
    });

    // [data-split-words] — word reveal
    gsap.utils.toArray<HTMLElement>('[data-split-words]').forEach((el) => {
      const words = splitWords(el);
      if (!words.length) return;
      gsap.fromTo(words,
        { y: reduced ? 0 : '100%' },
        {
          y: 0, duration: reduced ? 0.01 : 0.65, stagger: reduced ? 0 : 0.04, ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 85%', once: true },
        }
      );
    });

    /* ═══════ PARALLAX ═══════ */
    if (!reduced) {
      gsap.utils.toArray<HTMLElement>('[data-parallax]').forEach((el) => {
        const speed = parseFloat(el.getAttribute('data-speed') || '0.15');
        gsap.to(el, {
          yPercent: speed * 100,
          ease: 'none',
          scrollTrigger: {
            trigger: el.closest('section') || el,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1.5,
          },
        });
      });
    }

    /* ═══════ MAGNETIC BUTTONS ═══════ */
    if (!reduced) {
      gsap.utils.toArray<HTMLElement>('.magnetic-btn').forEach((btn) => {
        const onMove = (e: MouseEvent) => {
          const r = btn.getBoundingClientRect();
          const dx = ((e.clientX - r.left - r.width / 2) / r.width) * 40;
          const dy = ((e.clientY - r.top - r.height / 2) / r.height) * 40;
          gsap.to(btn, { x: dx, y: dy, duration: 0.3, ease: 'power2.out' });
        };
        const onLeave = () => { gsap.to(btn, { x: 0, y: 0, duration: 0.6, ease: 'elastic.out(1, 0.4)' }); };
        btn.addEventListener('mousemove', onMove);
        btn.addEventListener('mouseleave', onLeave);
        cleanups.push(() => { btn.removeEventListener('mousemove', onMove); btn.removeEventListener('mouseleave', onLeave); });
      });
    }

    /* ═══════ PAGE HERO (non-homepage) ═══════ */
    gsap.utils.toArray<HTMLElement>('[data-page-hero]').forEach((el) => {
      const heading = el.querySelector('h1, h2');
      const rest = el.querySelectorAll('p, .hero-action');
      if (heading && !reduced) {
        gsap.fromTo(heading, { y: 40, opacity: 0 }, { y: 0, opacity: 1, duration: 0.7, delay: 0.2, ease: 'power3.out' });
      }
      if (rest.length && !reduced) {
        gsap.fromTo(rest, { y: 30, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6, stagger: 0.1, delay: 0.5, ease: 'power3.out' });
      }
    });

    /* ═══════ SECTION SEPARATORS ═══════ */
    gsap.utils.toArray<HTMLElement>('[data-line]').forEach((line) => {
      gsap.fromTo(line,
        { scaleX: 0 },
        {
          scaleX: 1, duration: reduced ? 0.01 : 1, ease: 'power2.inOut',
          scrollTrigger: { trigger: line, start: 'top 90%', once: true },
        }
      );
    });

    return () => {
      lenis.destroy();
      gsap.ticker.remove(lenisRaf);
      ScrollTrigger.getAll().forEach((t) => t.kill());
      cleanups.forEach((fn) => fn());
    };
  }, [isLoaded]);

  return null;
}
